import React from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  Box,
  Slide,
  useMediaQuery,
} from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';

/**
 * MuiModal - A reusable Dialog/Modal component
 *
 * Jakob's Law: Uses familiar modal patterns with close button in top-right
 * Hick's Law: Limits actions to essential choices (typically 2-3 buttons)
 * Law of Proximity: Groups title, content, and actions in clear sections
 */

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: 12,
    boxShadow: '0 24px 48px rgba(0, 0, 0, 0.16)',
  },
}));

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(2, 3),
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

const StyledDialogContent = styled(DialogContent)(({ theme, noPadding }) => ({
  padding: noPadding ? 0 : theme.spacing(3),
}));

const StyledDialogActions = styled(DialogActions)(({ theme }) => ({
  padding: theme.spacing(2, 3),
  borderTop: `1px solid ${theme.palette.divider}`,
  gap: theme.spacing(1),
}));

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const MuiModal = ({
  open,
  onClose,
  title,
  subtitle,
  children,
  actions,
  maxWidth = 'sm',
  fullWidth = true,
  fullScreen = false,
  fullScreenOnMobile = false,
  showCloseButton = true,
  disableBackdropClick = false,
  disableEscapeKeyDown = false,
  scroll = 'paper',
  noPadding = false,
  titleIcon,
  dividers = false,
  transitionDuration = 225,
  keepMounted = false,
  titleProps = {},
  contentProps = {},
  actionsProps = {},
  PaperProps = {},
  sx = {},
  ...props
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const shouldFullScreen = fullScreen || (fullScreenOnMobile && isMobile);

  const handleClose = (event, reason) => {
    if (disableBackdropClick && reason === 'backdropClick') {
      return;
    }
    if (disableEscapeKeyDown && reason === 'escapeKeyDown') {
      return;
    }
    onClose?.(event, reason);
  };

  return (
    <StyledDialog
      open={open}
      onClose={handleClose}
      maxWidth={maxWidth}
      fullWidth={fullWidth}
      fullScreen={shouldFullScreen}
      scroll={scroll}
      TransitionComponent={Transition}
      transitionDuration={transitionDuration}
      keepMounted={keepMounted}
      PaperProps={{
        ...PaperProps,
        sx: {
          ...(shouldFullScreen && {
            borderRadius: 0,
          }),
          ...PaperProps?.sx,
        },
      }}
      sx={sx}
      {...props}
    >
      {(title || showCloseButton) && (
        <StyledDialogTitle {...titleProps}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            {titleIcon && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'primary.main',
                }}
              >
                {titleIcon}
              </Box>
            )}
            <Box>
              <Typography variant="h6" component="span" fontWeight={600}>
                {title}
              </Typography>
              {subtitle && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ display: 'block', mt: 0.25 }}
                >
                  {subtitle}
                </Typography>
              )}
            </Box>
          </Box>
          {showCloseButton && (
            <IconButton
              aria-label="close"
              onClick={onClose}
              size="small"
              sx={{
                color: 'text.secondary',
                '&:hover': {
                  color: 'text.primary',
                  backgroundColor: 'action.hover',
                },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          )}
        </StyledDialogTitle>
      )}
      <StyledDialogContent
        noPadding={noPadding}
        dividers={dividers}
        {...contentProps}
      >
        {children}
      </StyledDialogContent>
      {actions && (
        <StyledDialogActions {...actionsProps}>
          {actions}
        </StyledDialogActions>
      )}
    </StyledDialog>
  );
};

MuiModal.propTypes = {
  /** Controls modal visibility */
  open: PropTypes.bool.isRequired,
  /** Callback when modal should close */
  onClose: PropTypes.func,
  /** Modal title */
  title: PropTypes.node,
  /** Modal subtitle */
  subtitle: PropTypes.node,
  /** Modal content */
  children: PropTypes.node,
  /** Action buttons */
  actions: PropTypes.node,
  /** Maximum width of the modal */
  maxWidth: PropTypes.oneOf(['xs', 'sm', 'md', 'lg', 'xl', false]),
  /** If true, the modal stretches to maxWidth */
  fullWidth: PropTypes.bool,
  /** If true, the modal is full screen */
  fullScreen: PropTypes.bool,
  /** If true, the modal is full screen on mobile devices */
  fullScreenOnMobile: PropTypes.bool,
  /** Show close button in header */
  showCloseButton: PropTypes.bool,
  /** Disable closing by clicking backdrop */
  disableBackdropClick: PropTypes.bool,
  /** Disable closing with escape key */
  disableEscapeKeyDown: PropTypes.bool,
  /** Scroll behavior */
  scroll: PropTypes.oneOf(['paper', 'body']),
  /** Remove padding from content */
  noPadding: PropTypes.bool,
  /** Icon to display before title */
  titleIcon: PropTypes.node,
  /** Show dividers around content */
  dividers: PropTypes.bool,
  /** Transition duration in ms */
  transitionDuration: PropTypes.number,
  /** Keep modal mounted when closed */
  keepMounted: PropTypes.bool,
  /** Additional props for DialogTitle */
  titleProps: PropTypes.object,
  /** Additional props for DialogContent */
  contentProps: PropTypes.object,
  /** Additional props for DialogActions */
  actionsProps: PropTypes.object,
  /** Props for the Paper component */
  PaperProps: PropTypes.object,
  /** Custom styles */
  sx: PropTypes.object,
};

export default MuiModal;
